import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatStepper } from '@angular/material/stepper';
import { CareTeamTemplateService } from '../../../../../services/careTeamTemplate.service';
import { PatientService } from '../../../../../services/patient.service';
import { AddPatientService } from '../../../../../services/addpatient.service';
import {
  ICareTeamLookupDropdown,
  ICareTeamTemplateUserDropdown,
} from '../../../../../interfaces/ICareTeamTemplateDropdownData';
import {
  IAddCareTeamTemplate,
  IAddCareTeamTemplateParticipant,
} from '../../../../../interfaces/IAddCareTeamTemplate';
import { IExternalCymaDoctorDropdown } from '../../../../../interfaces/ExternalDoctors/IExternalCymaDoctorDropdown';
import { IExternalDoctor } from '../../../../../interfaces/ExternalDoctors/IExternalDoctor';
import { SwalToastService } from 'src/app/services/swal.service';
import { UnsavedChangesGuard } from 'src/app/guards/unsaved-changes.guard';

export type CareTeamMemberTab = 'internal' | 'external';

@Component({
  selector: 'app-care-team-template-form',
  templateUrl: './care-team-template-form.component.html',
  styleUrls: ['./care-team-template-form.component.css'],
  standalone: false,
})
export class CareTeamTemplateFormComponent implements OnInit {
  @Input() templateId?: number;
  @Output() closed = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();

  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: BeforeUnloadEvent) {
    if (this.hasUnsavedChanges()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  public form!: FormGroup;

  public categoryOptions: ICareTeamLookupDropdown[] = [];
  public roleOptions: ICareTeamLookupDropdown[] = [];
  public userOptions: ICareTeamTemplateUserDropdown[] = [];
  public externalDoctorCymaOptions: IExternalCymaDoctorDropdown[] = [];
  public externalDoctorOptions: IExternalDoctor[] = [];

  public isEditMode: boolean = false;
  public loading: boolean = false;
  public saving: boolean = false;

  public showAddMemberPanel: boolean = false;
  public pendingParticipant!: FormGroup;
  public editingParticipantIndex: number | null = null;
  public activeMemberTab: CareTeamMemberTab = 'internal';
  public useCymaList: boolean = false;

  @ViewChild('addMemberPanel') addMemberPanelRef?: ElementRef<HTMLElement>;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly careTeamTemplateService: CareTeamTemplateService,
    private readonly patientService: PatientService,
    private readonly addPatientService: AddPatientService,
    private readonly swalToastService: SwalToastService,
    private readonly unsavedChangesGuard: UnsavedChangesGuard,
  ) {}

  ngOnInit() {
    this.isEditMode = !!this.templateId;
    this.buildForm();
    this.fetchDropdownData();
    this.fetchExternalDoctorCymaOptions();
    this.fetchExternalDoctorOptions();

    if (this.isEditMode) {
      this.loadTemplate();
    }
  }

  buildForm() {
    this.form = this.formBuilder.group({
      name: ['', [Validators.required, Validators.maxLength(100)]],
      careTeamCategoryId: [null, Validators.required],
      note: [''],
      participants: this.formBuilder.array([]),
    });
    this.pendingParticipant = this.createParticipantGroup();
  }

  get participants(): FormArray {
    return this.form.get('participants') as FormArray;
  }

  createParticipantGroup(
    participant?: IAddCareTeamTemplateParticipant,
  ): FormGroup {
    const participantType: CareTeamMemberTab =
      participant?.participantType === 'External' ? 'external' : 'internal';

    return this.formBuilder.group({
      id: [participant?.id ?? null],
      participantType: [participantType],
      userId: [participant?.userId ?? null],
      externalDoctorId: [participant?.externalDoctorId ?? null],
      externalDoctorCymaId: [participant?.externalDoctorCymaId ?? null],
      careTeamRoleId: [
        participant?.careTeamRoleId ?? null,
        Validators.required,
      ],
      displayOrder: [
        participant?.displayOrder ?? null,
        [Validators.required, Validators.min(1)],
      ],
    });
  }

  selectMemberTab(tab: CareTeamMemberTab) {
    this.activeMemberTab = tab;
    this.pendingParticipant.patchValue({
      participantType: tab,
      userId: null,
      externalDoctorId: null,
      externalDoctorCymaId: null,
    });
  }

  toggleCymaList(useCymaList: boolean) {
    this.useCymaList = useCymaList;
    this.pendingParticipant.patchValue({
      externalDoctorId: null,
      externalDoctorCymaId: null,
    });
  }

  addParticipant() {
    this.participants.push(this.createParticipantGroup());
  }

  removeParticipant(index: number) {
    this.participants.removeAt(index);
    this.participants.markAsDirty();
  }

  sortedParticipantIndices(): number[] {
    return this.participants.controls
      .map((control, index) => ({ index, displayOrder: control.value.displayOrder }))
      .sort((a, b) => {
        const aOrder = a.displayOrder ?? Infinity;
        const bOrder = b.displayOrder ?? Infinity;
        return aOrder - bOrder;
      })
      .map((entry) => entry.index);
  }

  openAddMemberPanel() {
    this.editingParticipantIndex = null;
    this.activeMemberTab = 'internal';
    this.useCymaList = false;
    this.pendingParticipant = this.createParticipantGroup();
    this.showAddMemberPanel = true;
    setTimeout(() =>
      this.addMemberPanelRef?.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      }),
    );
  }

  openEditMemberPanel(index: number) {
    this.editingParticipantIndex = index;
    const participant = this.participants.at(index).value;
    this.activeMemberTab =
      participant?.externalDoctorId || participant?.externalDoctorCymaId
        ? 'external'
        : 'internal';
    this.useCymaList = !!participant?.externalDoctorCymaId;
    this.pendingParticipant = this.createParticipantGroup(participant);
    this.showAddMemberPanel = true;
  }

  confirmAddMember() {
    const isExternal = this.activeMemberTab === 'external';
    const memberIdControl = isExternal
      ? this.pendingParticipant.get(
          this.useCymaList ? 'externalDoctorCymaId' : 'externalDoctorId',
        )
      : this.pendingParticipant.get('userId');

    this.pendingParticipant.markAllAsTouched();

    if (memberIdControl?.invalid || !memberIdControl?.value) {
      this.swalToastService.toastError(
        isExternal
          ? 'External doctor is required'
          : 'User is required',
      );
      return;
    }

    if (
      this.pendingParticipant.get('careTeamRoleId')?.invalid ||
      this.pendingParticipant.get('displayOrder')?.invalid
    ) {
      return;
    }

    if (
      this.isDisplayOrderTaken(
        this.pendingParticipant.value.displayOrder,
        this.editingParticipantIndex,
      )
    ) {
      this.swalToastService.toastError(
        'This display order is already assigned to another member',
      );
      return;
    }

    if (
      this.isMemberAlreadyAdded(
        this.pendingParticipant.value,
        this.editingParticipantIndex,
      )
    ) {
      this.swalToastService.toastError(
        'This user cannot be added to the care team more than once, even with a different role',
      );
      return;
    }

    const participantValue = {
      ...this.pendingParticipant.value,
      participantType: isExternal ? 'External' : 'Internal',
      userId: isExternal ? null : this.pendingParticipant.value.userId,
      externalDoctorId:
        isExternal && !this.useCymaList
          ? this.pendingParticipant.value.externalDoctorId
          : null,
      externalDoctorCymaId:
        isExternal && this.useCymaList
          ? this.pendingParticipant.value.externalDoctorCymaId
          : null,
    };

    if (this.editingParticipantIndex !== null) {
      this.participants.at(this.editingParticipantIndex).patchValue(participantValue);
    } else {
      this.participants.push(this.createParticipantGroup(participantValue));
    }

    this.participants.markAsDirty();
    this.cancelAddMember();
  }

  isDisplayOrderTaken(
    displayOrder: number | null,
    excludeIndex: number | null,
  ): boolean {
    if (!displayOrder) {
      return false;
    }

    return this.participants.controls.some(
      (control, index) =>
        index !== excludeIndex && control.value.displayOrder === displayOrder,
    );
  }

  isMemberAlreadyAdded(
    pendingValue: {
      userId?: string | null;
      externalDoctorId?: number | null;
      externalDoctorCymaId?: number | null;
    },
    excludeIndex: number | null,
  ): boolean {
    const { userId, externalDoctorId, externalDoctorCymaId } = pendingValue;

    if (!userId && !externalDoctorId && !externalDoctorCymaId) {
      return false;
    }

    return this.participants.controls.some((control, index) => {
      if (index === excludeIndex) {
        return false;
      }

      if (userId) {
        return control.value.userId === userId;
      }
      if (externalDoctorId) {
        return control.value.externalDoctorId === externalDoctorId;
      }
      return control.value.externalDoctorCymaId === externalDoctorCymaId;
    });
  }

  cancelAddMember() {
    this.showAddMemberPanel = false;
    this.editingParticipantIndex = null;
    this.activeMemberTab = 'internal';
    this.useCymaList = false;
    this.pendingParticipant = this.createParticipantGroup();
  }

  getUserDisplayName(userId: string): string {
    return this.userOptions.find((user) => user.id === userId)?.displayName || '';
  }

  getExternalDoctorCymaDisplayName(externalDoctorCymaId: number): string {
    const doctor = this.externalDoctorCymaOptions.find(
      (option) => option.id === externalDoctorCymaId,
    );
    return doctor ? `${doctor.firstName} ${doctor.lastName}` : '';
  }

  getExternalDoctorDisplayName(externalDoctorId: number): string {
    const doctor = this.externalDoctorOptions.find(
      (option) => option.id === externalDoctorId,
    );
    return doctor ? `${doctor.firstName} ${doctor.lastName}` : '';
  }

  getParticipantDisplayName(participant: {
    userId?: string | null;
    externalDoctorId?: number | null;
    externalDoctorCymaId?: number | null;
  }): string {
    if (participant.externalDoctorCymaId) {
      return this.getExternalDoctorCymaDisplayName(
        participant.externalDoctorCymaId,
      );
    }
    if (participant.externalDoctorId) {
      return this.getExternalDoctorDisplayName(participant.externalDoctorId);
    }
    return this.getUserDisplayName(participant.userId || '');
  }

  getRoleDisplay(careTeamRoleId: number): string {
    return (
      this.roleOptions.find((role) => role.id === careTeamRoleId)?.display ||
      ''
    );
  }

  getInitials(displayName: string): string {
    if (!displayName) {
      return '';
    }

    const parts = displayName.trim().split(/\s+/);
    const initials =
      parts.length > 1
        ? parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
        : parts[0].charAt(0);

    return initials.toUpperCase();
  }

  fetchDropdownData() {
    this.careTeamTemplateService.getDropdownData().subscribe({
      next: (res) => {
        this.categoryOptions = res?.data?.categories || [];
        this.roleOptions = res?.data?.roles || [];
        this.userOptions = res?.data?.users || [];
      },
      error: (err) => {
        console.error('Error fetching care team dropdown data:', err);
      },
    });
  }

  fetchExternalDoctorCymaOptions() {
    this.patientService.getExternalCymaDoctors().subscribe({
      next: (doctors) => {
        this.externalDoctorCymaOptions = doctors || [];
      },
      error: (err) => {
        console.error('Error fetching CyMA doctors:', err);
      },
    });
  }

  fetchExternalDoctorOptions() {
    this.addPatientService.getPatientsDropDown().subscribe({
      next: (res) => {
        this.externalDoctorOptions = res?.data?.externalDoctors || [];
      },
      error: (err) => {
        console.error('Error fetching external doctors:', err);
      },
    });
  }

  loadTemplate() {
    this.loading = true;
    this.careTeamTemplateService
      .getCareTeamTemplateById(this.templateId!)
      .subscribe({
        next: (res) => {
          const template = res.data;
          this.form.patchValue({
            name: template.name,
            careTeamCategoryId: template.careTeamCategoryId,
            note: template.note,
          });

          this.participants.clear();
          (template.participants || []).forEach((participant) => {
            this.participants.push(this.createParticipantGroup(participant));
          });

          this.loading = false;
        },
        error: (err) => {
          this.loading = false;
          console.error('Error loading care team template:', err);
        },
      });
  }

  save() {
    this.form.markAllAsTouched();

    if (this.form.invalid || this.participants.length === 0) {
      this.swalToastService.toastError(
        'Please fill all required fields and add at least one care team member',
      );
      return;
    }

    this.saving = true;

    if (this.isEditMode) {
      const body: IAddCareTeamTemplate = {
        name: this.form.value.name,
        careTeamCategoryId: this.form.value.careTeamCategoryId,
        note: this.form.value.note || undefined,
        participants: this.participants.controls.map((group) => ({
          id: group.value.id ?? undefined,
          participantType:
            group.value.participantType === 'External' ? 'External' : 'Internal',
          userId: group.value.userId ?? undefined,
          externalDoctorId: group.value.externalDoctorId ?? undefined,
          externalDoctorCymaId: group.value.externalDoctorCymaId ?? undefined,
          careTeamRoleId: group.value.careTeamRoleId,
          displayOrder: group.value.displayOrder ?? undefined,
        })),
      };

      this.careTeamTemplateService
        .updateCareTeamTemplate(this.templateId!, body)
        .subscribe({
          next: () => {
            this.swalToastService.toastSuccess(
              'Care team template updated successfully',
            );
            this.saving = false;
            this.saved.emit();
          },
          error: (err) => {
            this.swalToastService.toastError(
              err.error?.message || 'Unable to update care team template',
            );
            this.saving = false;
            console.error('Error updating care team template:', err);
          },
        });
    } else {
      const body: IAddCareTeamTemplate = {
        name: this.form.value.name,
        careTeamCategoryId: this.form.value.careTeamCategoryId,
        note: this.form.value.note || undefined,
        participants: this.participants.controls.map((group) => ({
          participantType:
            group.value.participantType === 'External' ? 'External' : 'Internal',
          userId: group.value.userId ?? undefined,
          externalDoctorId: group.value.externalDoctorId ?? undefined,
          externalDoctorCymaId: group.value.externalDoctorCymaId ?? undefined,
          careTeamRoleId: group.value.careTeamRoleId,
          displayOrder: group.value.displayOrder ?? undefined,
        })),
      };

      this.careTeamTemplateService.addCareTeamTemplate(body).subscribe({
        next: () => {
          this.swalToastService.toastSuccess(
            'Care team template created successfully',
          );
          this.saving = false;
          this.saved.emit();
        },
        error: (err) => {
          this.swalToastService.toastError(
            err.error?.message || 'Unable to create care team template',
          );
          this.saving = false;
          console.error('Error creating care team template:', err);
        },
      });
    }
  }

  hasUnsavedChanges(): boolean {
    return (
      this.form.dirty || (this.showAddMemberPanel && this.pendingParticipant.dirty)
    );
  }

  cancel() {
    if (this.hasUnsavedChanges()) {
      this.unsavedChangesGuard.showDiscardChangesDialog().subscribe((confirmed) => {
        if (confirmed) {
          this.closed.emit();
        }
      });
      return;
    }

    this.closed.emit();
  }

  nextStep(stepper: MatStepper, stepIncrement: number): void {
    const currentStep = stepper.selectedIndex;
    const nextStepIndex = currentStep + stepIncrement;

    if (currentStep === 0 && stepIncrement === 1) {
      this.form.get('name')?.markAsTouched();
      this.form.get('careTeamCategoryId')?.markAsTouched();

      if (
        this.form.get('name')?.invalid ||
        this.form.get('careTeamCategoryId')?.invalid
      ) {
        this.swalToastService.toastError('Please fill all required fields');
        return;
      }
    }

    if (nextStepIndex >= 0 && nextStepIndex < stepper._steps.length) {
      stepper.selectedIndex = nextStepIndex;
    }
  }
}
